import { useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getBaseURL } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export const useChatStream = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]); 
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadSession = (id: string, msgs: ChatMessage[]) => {
    setSessionId(id);
    setMessages(msgs);
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  const sendMessage = async (input: string) => {
    const newHistory: ChatMessage[] = [...messages, { role: 'user', content: input }];
    setMessages(newHistory);
    setIsLoading(true);

    let incomingText = "";
    const token = localStorage.getItem('token');
    const baseUrl = getBaseURL(ApiEndPoint.Auth);

    try {
      await fetchEventSource(`${baseUrl}ai/chat`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          messages: newHistory,
          sessionId 
        }),
        onmessage(ev) {
          if (ev.data === '[DONE]') {
             setIsLoading(false);
             return;
          }
          try {
            const parsed = JSON.parse(ev.data);
            
            // Handle Session ID if returned (usually first message)
            if (parsed.sessionId) {
              setSessionId(parsed.sessionId);
              return;
            }

            if (parsed.content) {
                incomingText += parsed.content;
                // Functional state update to append text to the last message
                setMessages(prev => {
                  const last = prev[prev.length - 1];
                  if (last && last.role === 'assistant') {
                      return [...prev.slice(0, -1), { role: 'assistant', content: incomingText }];
                  }
                  return [...prev, { role: 'assistant', content: incomingText }];
                });
            }
          } catch (e) {
              console.error("Error parsing SSE message", e);
          }
        },
        onerror(err) { 
            console.error("Stream error", err);
            setIsLoading(false);
            throw err; 
        }
      });
    } catch (err) {
      console.error("Stream failed", err);
      setIsLoading(false);
    }
  };

  return { 
    messages, 
    sendMessage, 
    isLoading,
    sessionId,
    loadSession,
    startNewSession
  };
};
